import { getFacetValueFromQuery } from '../../helpers/utils';

interface SnowChainFacetOptions {
  purpose: string[];
  width: string[];
  series: string[];
  diameter: string[];
}

interface SnowChainFacetValue {
  purpose: string;
  width: string;
  series: string;
  diameter: string;
}

export default class SnowChainsFacetFilter {

  private options: SnowChainFacetOptions;
  private readonly purposeSelect: HTMLSelectElement;
  private readonly widthSelect: HTMLSelectElement;
  private readonly seriesSelect: HTMLSelectElement;
  private readonly diameterSelect: HTMLSelectElement;
  private readonly valueInput: HTMLInputElement;

  private readonly initialValue: SnowChainFacetValue;

  constructor(container: HTMLElement = document.querySelector('.js-snowChainsFacet') as HTMLElement) {
    const { options } = container.dataset;

    this.purposeSelect = container.querySelector('.js-snowChainPurpose') as HTMLSelectElement;
    this.widthSelect = container.querySelector('.js-snowChainWidth') as HTMLSelectElement;
    this.seriesSelect = container.querySelector('.js-snowChainSeries') as HTMLSelectElement;
    this.diameterSelect = container.querySelector('.js-snowChainDiameter') as HTMLSelectElement;
    this.valueInput = container.querySelector('.js-valueCombined') as HTMLInputElement;

    const initialValueRaw = getFacetValueFromQuery('snowChains');
    this.valueInput.value = initialValueRaw ?? '';
    this.initialValue = SnowChainsFacetFilter.parseValue(initialValueRaw);

    try {
      this.options = SnowChainsFacetFilter.parseOptions(options);
    } catch (e) {
      console.error(e.message); // eslint-disable-line
      this.options = {
        purpose: [],
        width: [],
        series: [],
        diameter: [],
      };
    }

    this.populate();
    this.bindUpdateValue();
  }

  private bindUpdateValue(): void {
    const updateValue = () => {
      this.valueInput.value = this.getValue();
    };

    [this.purposeSelect, this.widthSelect, this.seriesSelect, this.diameterSelect].forEach((element) => element.addEventListener('input', updateValue));
  }

  private static parseValue(rawValue: string | undefined): SnowChainFacetValue {
    try {
      const valueTokens = (rawValue as string).split('/');
      return {
        purpose: valueTokens[0] ?? '*',
        width: valueTokens[1] ?? '*',
        series: valueTokens[2] ?? '*',
        diameter: valueTokens[3] ?? '*',
      };
    } catch {
      return {
        purpose: '*',
        width: '*',
        series: '*',
        diameter: '*',
      };
    }
  }

  private static parseOptions(options: string | undefined): SnowChainFacetOptions {
    if (typeof options !== 'string') {
      throw new Error('Invalid type for options');
    }

    const optionsParsed = JSON.parse(options) as string[];
    if (!Array.isArray(optionsParsed)) {
      throw new Error('Options is not a JSON array');
    }

    const optionsDecomposed = (optionsParsed)
      .map((optionsParsed) => optionsParsed.split('/'))
      .reduce(({
        purpose,
        width,
        series,
        diameter,
      }, optionTokens) => {
        if (optionTokens.length !== 4) {
          // ignore malformed
          return {
            purpose,
            width,
            series,
            diameter,
          };
        }

        const [purposeValue, widthValue, seriesValue, diameterValue] = optionTokens;

        return {
          purpose: purpose.add(purposeValue),
          width: width.add(widthValue),
          series: series.add(seriesValue),
          diameter: diameter.add(diameterValue),
        };

      }, {
        purpose: new Set<string>(),
        width: new Set<string>(),
        series: new Set<string>(),
        diameter: new Set<string>(),
      });

    const optionsSorted = {
      purpose: Array.from(optionsDecomposed.purpose)
        .sort(),
      width: Array.from(optionsDecomposed.width)
        .sort(),
      series: Array.from(optionsDecomposed.series)
        .sort(),
      diameter: Array.from(optionsDecomposed.diameter)
        .sort(),
    };

    return optionsSorted;
  }

  private static addSelectOptions(select: HTMLSelectElement, options: string[], currentValue: string): void {
    options.forEach((optionValue) => {
      const optionEl = document.createElement('option');
      optionEl.textContent = optionValue;
      optionEl.value = optionValue;
      select.options.add(optionEl);
    });
    // eslint-disable-next-line no-param-reassign
    select.value = currentValue;
  }

  private populate(): void {
    SnowChainsFacetFilter.addSelectOptions(this.purposeSelect, this.options.purpose, this.initialValue.purpose);
    SnowChainsFacetFilter.addSelectOptions(this.widthSelect, this.options.width, this.initialValue.width);
    SnowChainsFacetFilter.addSelectOptions(this.seriesSelect, this.options.series, this.initialValue.series);
    SnowChainsFacetFilter.addSelectOptions(this.diameterSelect, this.options.diameter, this.initialValue.diameter);
  }

  getValue(): string {
    return `${this.purposeSelect.value}/${this.widthSelect.value}/${this.seriesSelect.value}/${this.diameterSelect.value}`;
  }
}
